import React from 'react';

// Product images mapping (same as ProductSelectionStep)
const productImages = {
  'windows': 'https://foxwindowsanddoors.com/app/uploads/form/windows.png',
  'internal-products': 'https://foxwindowsanddoors.com/app/uploads/form/AluminiumDoors.png',
  'front-back-doors': 'https://foxwindowsanddoors.com/app/uploads/2024/03/Spitfire-Front-Doors.jpg',
  'bifolding-sliding-french-doors': 'https://foxwindowsanddoors.com/app/uploads/form/Bifolding.jpg',
  'conservatories': 'https://foxwindowsanddoors.com/app/uploads/form/Conservatories & Extensions.png',
  'conservatory-roofs': 'https://foxwindowsanddoors.com/app/uploads/form/Conservatory Roofs.png',
  'verandas-pergolas': 'https://foxwindowsanddoors.com/app/uploads/form/Verandas.png',
};

const SummaryStep = ({
  formData,
  firstName,
  lastName,
  email,
  phone,
  postcode,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneChange,
  onPostcodeChange,
  errors
}) => {
  // Format the design specifications for display
  const getDisplayValue = (value) => {
    if (!value) return 'Not specified';
    return value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get the product image based on selected product
  const getProductImage = () => {
    if (formData.product && productImages[formData.product]) {
      return productImages[formData.product];
    }
    return 'https://foxwindowsanddoors.com/app/uploads/form/windows.png'; // Default fallback
  };

  return (
    <div className="w-full">
      {/* Title */}
      <h2 className="mb-12 text-center text-[24px] font-bold text-black">
        Review Your Project Requirements
      </h2>

      {/* Two Column Layout */}
      <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-6 px-4 md:grid-cols-2">
        {/* Left Column - Design Summary with Image */}
        <div className="relative h-[600px] overflow-hidden rounded-lg">
          {/* Background Image with Gradient Overlay */}
          <img
            src={getProductImage()}
            alt="Your Project Requirements"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20"></div>

          {/* Design Details Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <h3 className="mb-6 text-[28px] font-bold text-white">Your Project Requirements</h3>

            <div className="space-y-4">
              {formData.product && (
                <div>
                  <p className="text-[14px] text-white/70">Product</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.product)}</p>
                </div>
              )}

              {formData.style && (
                <div>
                  <p className="text-[14px] text-white/70">Style</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.style)}</p>
                </div>
              )}

              {formData.roofType && (
                <div>
                  <p className="text-[14px] text-white/70">Roof Type</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.roofType)}</p>
                </div>
              )}

              {formData.newRoofType && (
                <div>
                  <p className="text-[14px] text-white/70">New Roof Type</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.newRoofType)}</p>
                </div>
              )}

              {(formData.width && formData.height) && (
                <div>
                  <p className="text-[14px] text-white/70">Dimensions</p>
                  <p className="text-[18px] font-semibold text-white">{formData.width}m × {formData.height}m</p>
                </div>
              )}

              {formData.colour && (
                <div>
                  <p className="text-[14px] text-white/70">Colour</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.colour)}</p>
                </div>
              )}

              {formData.hardware && (
                <div>
                  <p className="text-[14px] text-white/70">Hardware</p>
                  <p className="text-[18px] font-semibold text-white">{getDisplayValue(formData.hardware)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Contact Form */}
        <div className="flex flex-col justify-center rounded-lg bg-gray-100 p-12">
          <h3 className="mb-8 text-center text-[24px] font-bold text-black">
            Enter Your Details
          </h3>

          {/* First Name */}
          <div className="mb-4">
            <label className="mb-2 block text-[15px] text-[#4c4c4c]">
              First Name*
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
              placeholder="Enter your first name"
              className="w-full border border-[#d9d9d9] bg-white px-6 py-3 text-[15px] text-[#252525] placeholder:text-[#9ca2ae] focus:border-[#ED6B64] focus:outline-none"
            />
            {errors?.firstName && (
              <p className="mt-2 text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="mb-4">
            <label className="mb-2 block text-[15px] text-[#4c4c4c]">
              Last Name*
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
              placeholder="Enter your last name"
              className="w-full border border-[#d9d9d9] bg-white px-6 py-3 text-[15px] text-[#252525] placeholder:text-[#9ca2ae] focus:border-[#ED6B64] focus:outline-none"
            />
            {errors?.lastName && (
              <p className="mt-2 text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="mb-2 block text-[15px] text-[#4c4c4c]">
              Email Address*
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="Enter your email"
              className="w-full border border-[#d9d9d9] bg-white px-6 py-3 text-[15px] text-[#252525] placeholder:text-[#9ca2ae] focus:border-[#ED6B64] focus:outline-none"
            />
            {errors?.email && (
              <p className="mt-2 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="mb-2 block text-[15px] text-[#4c4c4c]">
              Phone Number*
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="Enter your phone number"
              className="w-full border border-[#d9d9d9] bg-white px-6 py-3 text-[15px] text-[#252525] placeholder:text-[#9ca2ae] focus:border-[#ED6B64] focus:outline-none"
            />
            {errors?.phone && (
              <p className="mt-2 text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Postcode */}
          <div className="mb-6">
            <label className="mb-2 block text-[15px] text-[#4c4c4c]">
              Postcode*
            </label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => onPostcodeChange(e.target.value)}
              placeholder="Enter your postcode"
              className="w-full border border-[#d9d9d9] bg-white px-6 py-3 text-[15px] text-[#252525] placeholder:text-[#9ca2ae] focus:border-[#ED6B64] focus:outline-none"
            />
            {errors?.postcode && (
              <p className="mt-2 text-sm text-red-500">{errors.postcode}</p>
            )}
          </div>

          {/* Data Protection Notice */}
          <p className="mx-auto mb-6 text-center text-xs text-[#4c4c4c]">
            By submitting this form you agree to your details being used to respond to your enquiry. For more information, please read our <a href="/privacy-policy/" target="_blank" className="text-primary font-bold hover:text-black tracking-wider">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};



export default SummaryStep;
